/**
 * Shared ArcheAIAction definitions for the Travel arche.
 *
 * Every travel feature should register its actions here with a stable feature
 * key, then `buildTravelAIActions` filters them based on the user's effective
 * feature map. Disabled features never reach the AI, which keeps the prompt
 * surface honest and prevents the model from calling tools the user turned off.
 *
 * Core actions that operate on trips / days / events / transits use
 * `featureKey: null` because they are the app itself — disabling them would
 * leave the AI unable to do anything useful. Feature-flagged extras
 * (dossier, map, flight status, budget, packing, reservations, critic,
 * share links, email inbox) each carry their own featureKey.
 */

import type { ArcheAIAction } from '@/components/fias-ai/types';

interface FeatureScopedAction extends ArcheAIAction {
  /** The feature key this action belongs to, or `null` for always-on actions. */
  featureKey: string | null;
}

// ============================================================================
// Traveler Profile (F1)
// ============================================================================

const UPDATE_PROFILE_ACTION: FeatureScopedAction = {
  id: 'update_profile',
  featureKey: 'traveler_profile',
  description:
    "Update the user's persistent traveler profile. Call this when the user reveals a lasting preference " +
    '(e.g., dietary restriction, mobility limit, budget level, interest, language). ' +
    'Only send fields that should change — omitted fields are left untouched. ' +
    'Use sparingly: do NOT call for transient trip details, only for preferences that apply across trips.',
  parameterSchema: {
    type: 'object',
    properties: {
      diet: { type: 'array', items: { type: 'string' } },
      allergies: { type: 'array', items: { type: 'string' } },
      mobility: { type: 'string', enum: ['low', 'moderate', 'high'] },
      pace: { type: 'string', enum: ['slow', 'moderate', 'packed'] },
      budget: { type: 'string', enum: ['shoestring', 'mid', 'premium', 'luxury'] },
      interests: { type: 'array', items: { type: 'string' } },
      dislikes: { type: 'array', items: { type: 'string' } },
      languages: { type: 'array', items: { type: 'string' } },
      homeCity: { type: 'string' },
      notes: { type: 'string' },
      criticFocusAreas: {
        type: 'array',
        items: { type: 'string' },
        description:
          'Extra focus areas the Trip Analysis critic should weigh. Only set when the user explicitly asks (e.g. "pay attention to kid-friendly stops").',
      },
    },
  },
  requiresConfirmation: false,
  category: 'profile',
};

const TOGGLE_FEATURE_ACTION: FeatureScopedAction = {
  id: 'toggle_feature',
  featureKey: null,
  description:
    'Enable or disable a Travel-arche feature. Only call when the user explicitly asks to turn something on or off. ' +
    'Valid keys: traveler_profile, map, dossier, email_inbox, flight_status, budget, packing_list, ' +
    'reservation_scout, critic.',
  parameterSchema: {
    type: 'object',
    properties: {
      key: { type: 'string' },
      enabled: { type: 'boolean' },
    },
    required: ['key', 'enabled'],
  },
  requiresConfirmation: false,
  category: 'features',
};

// ============================================================================
// Core Trip / Day / Transit / Event CRUD (no feature flag)
// ============================================================================

const CREATE_TRIP_ACTION: FeatureScopedAction = {
  id: 'create_trip',
  featureKey: null,
  description:
    'Create a new trip in a single call. Use this when the user says "start a trip to X" or describes a full itinerary up front — populate `initialDays` (with nested `events`) and `initialTransits` so the trip is fully built in one shot. After creation the returned tripId becomes the active trip context.',
  parameterSchema: {
    type: 'object',
    properties: {
      title: { type: 'string', description: 'Short trip title, e.g. "Paris Weekend".' },
      startDate: { type: 'string', description: 'ISO date YYYY-MM-DD.' },
      initialDays: {
        type: 'array',
        description:
          'Days to create in order (Day 1, Day 2, …). Each day may carry events. Omit when the user only asks to create an empty trip shell.',
        items: {
          type: 'object',
          properties: {
            dayTitle: { type: 'string' },
            city: { type: 'string' },
            country: { type: 'string' },
            accommodationName: { type: 'string' },
            accommodationAddress: { type: 'string' },
            accommodationCheckin: { type: 'string', description: 'ISO YYYY-MM-DD.' },
            accommodationCheckout: { type: 'string', description: 'ISO YYYY-MM-DD.' },
            events: {
              type: 'array',
              description:
                'Events to attach to this day in display order (sights, meals, activities, transport legs, hotel check-ins, notes).',
              items: {
                type: 'object',
                properties: {
                  title: { type: 'string' },
                  time: { type: 'string', description: 'Local time, e.g. "19:30".' },
                  eventType: {
                    type: 'string',
                    enum: ['sight', 'food', 'activity', 'transport', 'hotel', 'note'],
                  },
                  notes: { type: 'string' },
                },
                required: ['title'],
              },
            },
          },
        },
      },
      initialTransits: {
        type: 'array',
        description:
          'Transit segments (flights/trains/etc.) to append to the trip after the initial days, in chronological order. Omit when no transits should be added at creation time.',
        items: {
          type: 'object',
          properties: {
            transitFrom: { type: 'string' },
            transitTo: { type: 'string' },
            transitMode: {
              type: 'string',
              enum: ['Flight', 'Train', 'Bus', 'Drive', 'Ferry', 'Helicopter', 'Other'],
            },
            transitDeparture: { type: 'string', description: 'ISO timestamp or time string.' },
            transitArrival: { type: 'string', description: 'ISO timestamp or time string.' },
            transitNotes: {
              type: 'string',
              description: 'Confirmation number, seat assignment, flight number, etc.',
            },
          },
          required: ['transitMode'],
        },
      },
    },
  },
  requiresConfirmation: false,
  category: 'trip',
};

const UPDATE_TRIP_ACTION: FeatureScopedAction = {
  id: 'update_trip',
  featureKey: null,
  description: 'Rename the trip, shift its start date, or archive it.',
  parameterSchema: {
    type: 'object',
    properties: {
      title: { type: 'string' },
      startDate: { type: 'string', description: 'ISO YYYY-MM-DD.' },
      status: { type: 'string', enum: ['active', 'archived'] },
    },
  },
  requiresConfirmation: false,
  category: 'trip',
};

const DELETE_TRIP_ACTION: FeatureScopedAction = {
  id: 'delete_trip',
  featureKey: null,
  description:
    'Delete the entire trip and all its contents. Extremely destructive — only call after the user explicitly asks to delete or cancel the whole trip.',
  parameterSchema: { type: 'object', properties: {} },
  requiresConfirmation: true,
  category: 'trip',
};

const ADD_DAY_ACTION: FeatureScopedAction = {
  id: 'add_day',
  featureKey: null,
  description:
    'Append a NEW day to the trip — always lands at the end of the existing days. Use when the trip is genuinely getting longer. ' +
    'DO NOT use this as the "add" half of a "replace day X with something else" flow: appending an item ' +
    "and then leaving it at the end re-orders the user's itinerary. To swap a day's theme/city/contents " +
    'in place, call `update_item` on the existing day (and `delete_event` / `add_event` on its events) ' +
    "instead — that preserves Day N's position. If you truly must add+delete (e.g. day count is changing " +
    'too), follow add_day with `reorder_items` in the same turn to slot the new day into the correct slot ' +
    'before the existing day order is disturbed.',
  parameterSchema: {
    type: 'object',
    properties: {
      dayTitle: { type: 'string' },
      city: { type: 'string' },
      country: { type: 'string' },
      accommodationName: { type: 'string' },
      accommodationAddress: { type: 'string' },
      accommodationCheckin: { type: 'string', description: 'ISO YYYY-MM-DD check-in date.' },
      accommodationCheckout: { type: 'string', description: 'ISO YYYY-MM-DD check-out date.' },
    },
  },
  requiresConfirmation: false,
  category: 'items',
};

const ADD_TRANSIT_ACTION: FeatureScopedAction = {
  id: 'add_transit',
  featureKey: null,
  description: 'Append a transit segment (flight, train, bus, drive, etc.) to the trip.',
  parameterSchema: {
    type: 'object',
    properties: {
      transitFrom: { type: 'string' },
      transitTo: { type: 'string' },
      transitMode: {
        type: 'string',
        enum: ['Flight', 'Train', 'Bus', 'Drive', 'Ferry', 'Helicopter', 'Other'],
      },
      transitDeparture: { type: 'string', description: 'ISO timestamp or time string.' },
      transitArrival: { type: 'string', description: 'ISO timestamp or time string.' },
      transitNotes: {
        type: 'string',
        description: 'Confirmation number, seat assignment, flight number (e.g. "AA100"), etc.',
      },
    },
    required: ['transitMode'],
  },
  requiresConfirmation: false,
  category: 'items',
};

const UPDATE_ITEM_ACTION: FeatureScopedAction = {
  id: 'update_item',
  featureKey: null,
  description:
    'Edit fields on a day or a transit segment. Only the fields you set are changed. Use the itemId from the trip outline. ' +
    'PREFER this over `delete_item` + `add_day` when the user says "swap", "replace", "change", or ' +
    '"redo" a specific day — keep the same item, retitle / recity / re-accommodation it, then ' +
    "`delete_event` the old events and `add_event` the new ones. This preserves the day's position in " +
    'the itinerary; delete+add would silently move the new day to the end of the trip.',
  parameterSchema: {
    type: 'object',
    properties: {
      itemId: { type: 'string', description: 'Target item (`tri_…`).' },
      // Day fields
      dayTitle: { type: 'string' },
      city: { type: 'string' },
      country: { type: 'string' },
      accommodationName: { type: 'string' },
      accommodationAddress: { type: 'string' },
      accommodationCheckin: { type: 'string' },
      accommodationCheckout: { type: 'string' },
      // Transit fields
      transitFrom: { type: 'string' },
      transitTo: { type: 'string' },
      transitMode: {
        type: 'string',
        enum: ['Flight', 'Train', 'Bus', 'Drive', 'Ferry', 'Helicopter', 'Other'],
      },
      transitDeparture: { type: 'string' },
      transitArrival: { type: 'string' },
      transitNotes: { type: 'string' },
    },
    required: ['itemId'],
  },
  requiresConfirmation: false,
  category: 'items',
};

const DELETE_ITEM_ACTION: FeatureScopedAction = {
  id: 'delete_item',
  featureKey: null,
  description:
    'Delete a day or transit. Cascades to all its events + attachments. Only call when the user explicitly asks to remove this specific day or transit AND the trip is genuinely getting one day shorter. ' +
    'NEVER pair delete_item + add_day to "replace" a day — the new day will land at the end and shift the ' +
    "rest of the trip. To swap a day's contents in place, use `update_item` on the existing day plus " +
    '`delete_event` / `add_event` to change its events.',
  parameterSchema: {
    type: 'object',
    properties: { itemId: { type: 'string' } },
    required: ['itemId'],
  },
  requiresConfirmation: true,
  category: 'items',
};

const DUPLICATE_DAY_ACTION: FeatureScopedAction = {
  id: 'duplicate_day',
  featureKey: null,
  description:
    'Copy a whole day (including events + attachments) and append it to the trip. Useful when the user says "do the same thing tomorrow" or wants a staging day.',
  parameterSchema: {
    type: 'object',
    properties: { itemId: { type: 'string', description: 'Source day (`tri_…`).' } },
    required: ['itemId'],
  },
  requiresConfirmation: false,
  category: 'items',
};

const REORDER_ITEMS_ACTION: FeatureScopedAction = {
  id: 'reorder_items',
  featureKey: null,
  description:
    "Reorder the trip's days + transits. Pass every itemId in the desired new order. Day numbers are renumbered server-side.",
  parameterSchema: {
    type: 'object',
    properties: {
      itemIds: { type: 'array', items: { type: 'string' } },
    },
    required: ['itemIds'],
  },
  requiresConfirmation: false,
  category: 'items',
};

const ADD_EVENT_ACTION: FeatureScopedAction = {
  id: 'add_event',
  featureKey: null,
  description:
    "Add an event (sight, meal, activity, transport leg, hotel check-in, note) to a day. Use the day's itemId from the trip outline.",
  parameterSchema: {
    type: 'object',
    properties: {
      itemId: { type: 'string', description: 'Target day (`tri_…`).' },
      title: { type: 'string' },
      time: { type: 'string', description: 'Local time, e.g. "19:30".' },
      eventType: {
        type: 'string',
        enum: ['sight', 'food', 'activity', 'transport', 'hotel', 'note'],
      },
      notes: { type: 'string' },
    },
    required: ['itemId', 'title'],
  },
  requiresConfirmation: false,
  category: 'events',
};

const UPDATE_EVENT_ACTION: FeatureScopedAction = {
  id: 'update_event',
  featureKey: null,
  description: 'Edit fields on a single event. Only the fields you set are changed.',
  parameterSchema: {
    type: 'object',
    properties: {
      eventId: { type: 'string' },
      title: { type: 'string' },
      time: { type: 'string' },
      eventType: {
        type: 'string',
        enum: ['sight', 'food', 'activity', 'transport', 'hotel', 'note'],
      },
      notes: { type: 'string' },
    },
    required: ['eventId'],
  },
  requiresConfirmation: false,
  category: 'events',
};

const DELETE_EVENT_ACTION: FeatureScopedAction = {
  id: 'delete_event',
  featureKey: null,
  description:
    'Delete an event. Only call when the user explicitly asks to remove this specific event.',
  parameterSchema: {
    type: 'object',
    properties: { eventId: { type: 'string' } },
    required: ['eventId'],
  },
  requiresConfirmation: true,
  category: 'events',
};

const MOVE_EVENT_ACTION: FeatureScopedAction = {
  id: 'move_event',
  featureKey: null,
  description: 'Move an event from its current day to another day on the same trip.',
  parameterSchema: {
    type: 'object',
    properties: {
      eventId: { type: 'string' },
      targetItemId: { type: 'string', description: 'Destination day (`tri_…`).' },
    },
    required: ['eventId', 'targetItemId'],
  },
  requiresConfirmation: false,
  category: 'events',
};

const DUPLICATE_EVENT_ACTION: FeatureScopedAction = {
  id: 'duplicate_event',
  featureKey: null,
  description: 'Copy an event to another day (or the same day).',
  parameterSchema: {
    type: 'object',
    properties: {
      eventId: { type: 'string' },
      targetItemId: { type: 'string', description: 'Destination day (`tri_…`).' },
    },
    required: ['eventId', 'targetItemId'],
  },
  requiresConfirmation: false,
  category: 'events',
};

const REORDER_EVENTS_ACTION: FeatureScopedAction = {
  id: 'reorder_events',
  featureKey: null,
  description: 'Reorder events within a day. Pass every eventId in the desired new order.',
  parameterSchema: {
    type: 'object',
    properties: {
      itemId: { type: 'string', description: 'Day the events belong to.' },
      eventIds: { type: 'array', items: { type: 'string' } },
    },
    required: ['itemId', 'eventIds'],
  },
  requiresConfirmation: false,
  category: 'events',
};

// ============================================================================
// Map (F3)
// ============================================================================

const GEOCODE_ITEM_ACTION: FeatureScopedAction = {
  id: 'geocode_item',
  featureKey: 'map',
  description:
    'Look up coordinates for a day or transit from a free-text query (city, address, landmark) and pin them on the map.',
  parameterSchema: {
    type: 'object',
    properties: {
      itemId: { type: 'string' },
      query: {
        type: 'string',
        description:
          'Free-text place to geocode, e.g. "Eiffel Tower, Paris" or "Hotel Lutetia, 45 Blvd Raspail".',
      },
    },
    required: ['itemId', 'query'],
  },
  requiresConfirmation: false,
  category: 'map',
};

const GEOCODE_EVENT_ACTION: FeatureScopedAction = {
  id: 'geocode_event',
  featureKey: 'map',
  description: 'Same as geocode_item but for a specific event.',
  parameterSchema: {
    type: 'object',
    properties: {
      eventId: { type: 'string' },
      query: { type: 'string' },
    },
    required: ['eventId', 'query'],
  },
  requiresConfirmation: false,
  category: 'map',
};

// ============================================================================
// Dossier (F3)
// ============================================================================

const REGENERATE_DOSSIER_ACTION: FeatureScopedAction = {
  id: 'regenerate_dossier',
  featureKey: 'dossier',
  description:
    'Regenerate the AI destination dossier for the currently-open trip. Only call when the user asks for a fresh dossier ' +
    'or after a significant trip change (new destination, shifted dates, added days). ' +
    'Do NOT call this proactively on every edit — it kicks off a multi-second web-search run.',
  parameterSchema: { type: 'object', properties: {} },
  requiresConfirmation: false,
  category: 'dossier',
};

// ============================================================================
// Email Inbox (F2)
// ============================================================================

const REPARSE_EMAIL_ACTION: FeatureScopedAction = {
  id: 'reparse_email',
  featureKey: 'email_inbox',
  description:
    'Re-run the parser on a forwarded email the user previously received. Useful when the original parse landed as `unassigned` or `error` and the user wants another try.',
  parameterSchema: {
    type: 'object',
    properties: { emailId: { type: 'string', description: 'Inbox email id (`trm_…`).' } },
    required: ['emailId'],
  },
  requiresConfirmation: false,
  category: 'email_inbox',
};

const ASSIGN_EMAIL_TO_TRIP_ACTION: FeatureScopedAction = {
  id: 'assign_email_to_trip',
  featureKey: 'email_inbox',
  description:
    'Assign a previously forwarded email to a specific trip and re-parse so its commands land on that trip. Use when the user says something like "put that hotel on my Rome trip".',
  parameterSchema: {
    type: 'object',
    properties: {
      emailId: { type: 'string' },
      tripId: { type: 'string' },
    },
    required: ['emailId', 'tripId'],
  },
  requiresConfirmation: false,
  category: 'email_inbox',
};

// ============================================================================
// Flight Status (F5)
// ============================================================================

const GET_FLIGHT_STATUS_ACTION: FeatureScopedAction = {
  id: 'get_flight_status',
  featureKey: 'flight_status',
  description:
    'Force-refresh live flight status for a specific transit. Use when the user asks "is my flight on time?" or "has AA100 landed?". Read-only. ' +
    'If the result is cancelled or diverted, proactively suggest recovery options in your next message.',
  parameterSchema: {
    type: 'object',
    properties: { itemId: { type: 'string', description: 'Transit item id (`tri_…`).' } },
    required: ['itemId'],
  },
  requiresConfirmation: false,
  category: 'flight_status',
};

// ============================================================================
// Budget + Expenses (F7)
// ============================================================================

const UPDATE_BUDGET_ACTION: FeatureScopedAction = {
  id: 'update_budget',
  featureKey: 'budget',
  description:
    "Set or change the trip's home currency and/or optional total cap. Pass totalCents: null to remove an existing cap. " +
    'When the user asks you to "fill out" / "plan" / "estimate" / "set up" the budget (not just "set my cap to $X"), ' +
    'call this once to set the home currency + cap AND in the same response call `add_expense` ' +
    'multiple times to seed estimated line items. ' +
    'GOAL: produce a COMPLETE estimated budget, not just the costs that happen to already exist on the trip. ' +
    'First read `budget.lineItems` in the state snapshot to see what is already recorded, then ADD ONLY THE ' +
    'MISSING costs — never duplicate a line item that is already there (e.g. if flights are already listed, ' +
    'skip flights but still add everything else). Cover every category that applies to this trip: ' +
    '• lodging — one line per night (or a multi-night block) for the whole stay; ' +
    '• transport — for FLIGHTS, do NOT guess the fare yourself: call `scout_flight_fares` so each leg is ' +
    'priced from live web search and round trips stay consistent. Then add the NON-flight transport here: ' +
    'airport transfers, local transit / rideshare / metro passes, rental car + fuel + parking, and ' +
    'intercity trains or ferries the itinerary implies; ' +
    "• food — each day's meals (a per-day breakfast/lunch/dinner estimate is fine if there's no specific reservation); " +
    '• activity — every marquee event on the itinerary plus a reasonable allowance for unplanned activities and entry fees; ' +
    '• other — trip-level costs that have no day: travel insurance, visa / entry fees, eSIM or roaming, tips, and a ' +
    'souvenirs / miscellaneous buffer. ' +
    'Walk the itinerary day by day so no day is left without food/transport coverage. Use plausible category averages ' +
    'for the destination. PLAN FOR ONE TRAVELER unless the user has told you how many people are going — default to a ' +
    'solo trip (single hotel occupancy, one cover per meal, one of each ticket) and only scale up when a count is given. ' +
    'Keep it consistent across categories: if you assume N travelers, meals, lodging, AND flights must all reflect N. ' +
    'Tag each estimate with the matching `itemId` / `eventId` so it lines up ' +
    'with the itinerary; trip-level "other" costs need no link. ' +
    'ASKING vs. PROCEEDING: if something would materially change the numbers and you genuinely cannot infer it from the ' +
    'trip (e.g. lodging tier, whether a rental car is needed, dining style), you MAY ask the user a ' +
    'short clarifying question or two first. But NEVER block on it — if the user does not answer, or asked you to "just ' +
    'estimate" / "do your best", proceed with sensible defaults and state the assumptions you made (e.g. "assumed 1 ' +
    'traveler, mid-range hotels, no rental car"). A best-effort complete budget beats an empty one waiting on answers.',
  parameterSchema: {
    type: 'object',
    properties: {
      homeCurrency: { type: 'string', description: 'ISO 4217 three-letter code, e.g. "EUR".' },
      totalCents: {
        type: ['integer', 'null'],
        description: 'Optional cap in integer cents. Pass null to clear.',
      },
    },
  },
  requiresConfirmation: false,
  category: 'budget',
};

const ADD_EXPENSE_ACTION: FeatureScopedAction = {
  id: 'add_expense',
  featureKey: 'budget',
  description:
    'Record an expense against the currently-open trip — works for BOTH actual spent costs AND planned ' +
    'estimates the user hasn\'t paid yet (e.g. "~$180 estimated for Friday dinner at Saigo Ramen"). ' +
    'During initial budget planning the user expects you to seed estimates for the FULL trip — hotel nights, ' +
    "airport transfers and local transport, each day's meals, marquee activities, and trip-level " +
    'costs like travel insurance / visas / tips / a miscellaneous buffer — not wait for them to type each one ' +
    'in. For FLIGHT fares, use `scout_flight_fares` (live web search) rather than adding them here by hand. ' +
    'Before adding, check `budget.lineItems` in the snapshot and skip anything already recorded so you ' +
    "don't create duplicates. Money is captured " +
    'in the native currency; the server converts to the home currency at incurredAt so summaries stay ' +
    'consistent. Set `incurredAt` to the day the expense will (or did) land — future dates are fine for ' +
    'estimates. Attach to an event via `eventId` or a day via `itemId` so the line item lines up with the ' +
    'itinerary. Make the description clearly flag estimates, e.g. "Hotel — 4 nights @ Hotel Albuquerque (est.)".',
  parameterSchema: {
    type: 'object',
    properties: {
      amountCents: { type: 'integer', description: 'Integer cents, e.g. 5000 for €50.00.' },
      currency: { type: 'string', description: 'ISO 4217 three-letter code.' },
      category: {
        type: 'string',
        enum: ['lodging', 'food', 'transport', 'activity', 'other'],
      },
      description: { type: 'string' },
      incurredAt: { type: 'string', description: 'ISO YYYY-MM-DD.' },
      itemId: { type: 'string' },
      eventId: { type: 'string' },
      paidBy: { type: 'string' },
      split: {
        type: 'object',
        properties: {
          participants: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                share: { type: 'number' },
              },
              required: ['name', 'share'],
            },
          },
          paidBy: { type: 'string' },
        },
      },
    },
    required: ['amountCents', 'currency', 'incurredAt'],
  },
  requiresConfirmation: false,
  category: 'budget',
};

const UPDATE_EXPENSE_ACTION: FeatureScopedAction = {
  id: 'update_expense',
  featureKey: 'budget',
  description:
    'Update fields on an existing expense. Changing amountCents / currency / incurredAt triggers a fresh FX lookup.',
  parameterSchema: {
    type: 'object',
    properties: {
      expenseId: { type: 'string' },
      amountCents: { type: 'integer' },
      currency: { type: 'string' },
      category: {
        type: 'string',
        enum: ['lodging', 'food', 'transport', 'activity', 'other'],
      },
      description: { type: 'string' },
      incurredAt: { type: 'string' },
      itemId: { type: 'string' },
      eventId: { type: 'string' },
      paidBy: { type: 'string' },
    },
    required: ['expenseId'],
  },
  requiresConfirmation: false,
  category: 'budget',
};

const DELETE_EXPENSE_ACTION: FeatureScopedAction = {
  id: 'delete_expense',
  featureKey: 'budget',
  description:
    'Delete an expense. Only call when the user explicitly asks to remove a specific expense.',
  parameterSchema: {
    type: 'object',
    properties: { expenseId: { type: 'string' } },
    required: ['expenseId'],
  },
  requiresConfirmation: true,
  category: 'budget',
};

// ============================================================================
// Packing / Todos (F6)
// ============================================================================

const GENERATE_PACKING_LIST_ACTION: FeatureScopedAction = {
  id: 'generate_packing_list',
  featureKey: 'packing_list',
  description:
    'Generate a trip-tailored packing list via AI. Call when the user says "help me pack" or similar. Dedupes against existing items, so re-running is safe. Do NOT enumerate items in your response — the UI renders the list.',
  parameterSchema: { type: 'object', properties: {} },
  requiresConfirmation: false,
  category: 'packing',
};

const ADD_TODO_ACTION: FeatureScopedAction = {
  id: 'add_todo',
  featureKey: 'packing_list',
  description:
    'Add a single todo item to the trip. Defaults to category="packing"; pass another category (e.g. "visa", "grocery") when the user groups todos into buckets.',
  parameterSchema: {
    type: 'object',
    properties: {
      title: { type: 'string' },
      notes: { type: 'string' },
      category: { type: 'string' },
    },
    required: ['title'],
  },
  requiresConfirmation: false,
  category: 'packing',
};

const TOGGLE_TODO_ACTION: FeatureScopedAction = {
  id: 'toggle_todo',
  featureKey: 'packing_list',
  description: 'Mark a todo complete or incomplete.',
  parameterSchema: {
    type: 'object',
    properties: {
      todoId: { type: 'string' },
      completed: { type: 'boolean' },
    },
    required: ['todoId', 'completed'],
  },
  requiresConfirmation: false,
  category: 'packing',
};

const DELETE_TODO_ACTION: FeatureScopedAction = {
  id: 'delete_todo',
  featureKey: 'packing_list',
  description: 'Remove a single todo.',
  parameterSchema: {
    type: 'object',
    properties: { todoId: { type: 'string' } },
    required: ['todoId'],
  },
  requiresConfirmation: false,
  category: 'packing',
};

// ============================================================================
// Reservations (F9)
// ============================================================================

const SCOUT_RESERVATIONS_ACTION: FeatureScopedAction = {
  id: 'scout_reservations',
  featureKey: 'reservation_scout',
  description:
    "Run the reservation scout over the trip's food events. Uses live web search to decide whether each restaurant needs advance booking. " +
    'Use when the user asks "do I need to book" or mentions a notoriously hard reservation. ' +
    'Scope: "trip" (default), "day" + itemId, or "event" + eventId.',
  parameterSchema: {
    type: 'object',
    properties: {
      scope: { type: 'string', enum: ['trip', 'day', 'event'] },
      itemId: { type: 'string' },
      eventId: { type: 'string' },
    },
  },
  requiresConfirmation: false,
  category: 'reservations',
};

const SCOUT_FLIGHT_FARES_ACTION: FeatureScopedAction = {
  id: 'scout_flight_fares',
  featureKey: 'budget',
  description:
    "Estimate realistic prices for the trip's flights using LIVE WEB SEARCH, and write them into the " +
    'budget as transport expenses (tagged so re-running replaces them, never duplicates). ' +
    'PREFER THIS over guessing flight prices yourself: when filling out / estimating the budget, do NOT ' +
    'hand-pick flight fares via `add_expense` — call this instead so each leg is grounded in real fares ' +
    'and a round trip stays consistent (no $650-out / $300-back mismatches). Runs over every flight leg ' +
    'on the trip. Optional `cabin` (economy | premium_economy | business | first, default economy) and ' +
    "`travelers` (per-person estimate; pass the trip's traveler count if known). After it runs, the new " +
    'flight line items show up in `budget.lineItems` on the next turn.',
  parameterSchema: {
    type: 'object',
    properties: {
      cabin: {
        type: 'string',
        enum: ['economy', 'premium_economy', 'business', 'first'],
      },
      travelers: { type: 'integer', description: 'Number of travelers (1-20).' },
    },
  },
  requiresConfirmation: false,
  category: 'budget',
};

// ============================================================================
// Trip Critic (F4)
// ============================================================================

const RUN_CRITIC_ACTION: FeatureScopedAction = {
  id: 'run_critic',
  featureKey: 'critic',
  description:
    'Run the sectioned Trip Analysis critique (Pacing, Logistics, Operating hours, Reservations, Health & jetlag, Missing basics). ' +
    'Use when the user asks "what do you think of this trip", "any problems with my plan", or similar. ' +
    'Output is rendered in the Trip Analysis drawer — do NOT restate the sections in your message.',
  parameterSchema: { type: 'object', properties: {} },
  requiresConfirmation: false,
  category: 'critic',
};

// ============================================================================
// Registry — keep alphabetical within each feature block
// ============================================================================

export const TRAVEL_FEATURE_ACTIONS: FeatureScopedAction[] = [
  // Profile + global toggles
  UPDATE_PROFILE_ACTION,
  TOGGLE_FEATURE_ACTION,
  // Core trip CRUD
  CREATE_TRIP_ACTION,
  UPDATE_TRIP_ACTION,
  DELETE_TRIP_ACTION,
  // Items (days + transits)
  ADD_DAY_ACTION,
  ADD_TRANSIT_ACTION,
  UPDATE_ITEM_ACTION,
  DELETE_ITEM_ACTION,
  DUPLICATE_DAY_ACTION,
  REORDER_ITEMS_ACTION,
  // Events
  ADD_EVENT_ACTION,
  UPDATE_EVENT_ACTION,
  DELETE_EVENT_ACTION,
  MOVE_EVENT_ACTION,
  DUPLICATE_EVENT_ACTION,
  REORDER_EVENTS_ACTION,
  // Map (F3)
  GEOCODE_ITEM_ACTION,
  GEOCODE_EVENT_ACTION,
  // Dossier (F3)
  REGENERATE_DOSSIER_ACTION,
  // Email inbox (F2)
  REPARSE_EMAIL_ACTION,
  ASSIGN_EMAIL_TO_TRIP_ACTION,
  // Flight status (F5)
  GET_FLIGHT_STATUS_ACTION,
  // Budget + expenses (F7)
  UPDATE_BUDGET_ACTION,
  ADD_EXPENSE_ACTION,
  UPDATE_EXPENSE_ACTION,
  DELETE_EXPENSE_ACTION,
  SCOUT_FLIGHT_FARES_ACTION,
  // Packing / todos (F6)
  GENERATE_PACKING_LIST_ACTION,
  ADD_TODO_ACTION,
  TOGGLE_TODO_ACTION,
  DELETE_TODO_ACTION,
  // Reservations (F9)
  SCOUT_RESERVATIONS_ACTION,
  // Critic (F4)
  RUN_CRITIC_ACTION,
  // Share links (F10)
];

/**
 * Filters the full action catalog down to just the ones whose feature is
 * enabled (or which are always-on). Missing feature keys default to enabled.
 */
export function buildTravelAIActions(
  features: Record<string, boolean>,
  extraActions: FeatureScopedAction[] = [],
): ArcheAIAction[] {
  const all = [...TRAVEL_FEATURE_ACTIONS, ...extraActions];
  return all
    .filter((a) => a.featureKey === null || features[a.featureKey] !== false)
    .map(({ featureKey: _fk, ...action }) => action);
}

export type { FeatureScopedAction };
